import React from 'react';
import { Box, Grow, List, ListItemButton } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { omitSpouseRelationship } from '../pedigree/edgeIntegrity';
import { EDGE_KINDS } from './edgeKinds';

function parseParentIds(from) {
  if (typeof from === 'string' && from.startsWith('union-')) {
    const parts = from.split('-');
    return [parseInt(parts[1], 10), parseInt(parts[2], 10)];
  }
  return [from];
}

function dedupe(members) {
  return Array.from(new Map(members.map((m) => [m.pid, m])).values());
}

// A detached node is no longer a twin of anyone — strip its twin data.
const clearTwinData = (m) => ({
  ...m,
  twinRelations: [],
  twinSubGroupId: null,
  siblingType: 'normal'
});

const sibTypeForSize = (n) =>
  n === 2 ? 'twin' : n === 3 ? 'triplet' : 'quadruplet';

function pruneAndRederiveTwins(siblingMembers, removedPids) {
  const removed = new Set(removedPids);

  const working = siblingMembers.map((m) => ({
    ...m,
    twinRelations: (m.twinRelations || []).map((r) => ({ ...r }))
  }));
  const byPid = new Map(working.map((m) => [m.pid, m]));
  removedPids.forEach((rpid) => {
    const partners = working
      .filter((m) => m.twinRelations.some((r) => r.withPid === rpid))
      .map((m) => m.pid)
      .sort((a, b) => a - b);
    for (let i = 0; i < partners.length - 1; i++) {
      const a = byPid.get(partners[i]);
      const b = byPid.get(partners[i + 1]);
      const alreadyLinked = a.twinRelations.some((r) => r.withPid === b.pid);
      if (!alreadyLinked) {
        a.twinRelations.push({ withPid: b.pid, type: 'dizygotic' });
        b.twinRelations.push({ withPid: a.pid, type: 'dizygotic' });
      }
    }
  });

  const pruned = working.map((m) => ({
    ...m,
    twinRelations: m.twinRelations.filter((r) => !removed.has(r.withPid))
  }));

  const ufParent = new Map();
  const find = (x) => {
    if (!ufParent.has(x)) ufParent.set(x, x);
    if (ufParent.get(x) !== x) ufParent.set(x, find(ufParent.get(x)));
    return ufParent.get(x);
  };
  const union = (a, b) => ufParent.set(find(a), find(b));
  const prunedByPid = new Map(pruned.map((m) => [m.pid, m]));
  pruned.forEach((m) => {
    m.twinRelations.forEach((r) => {
      const other = prunedByPid.get(r.withPid);
      const mutual =
        other && other.twinRelations.some((o) => o.withPid === m.pid);
      if (mutual) union(m.pid, r.withPid);
    });
  });

  const groupSize = new Map();
  pruned.forEach((m) => {
    if (!m.twinRelations.length) return;
    const root = find(m.pid);
    groupSize.set(root, (groupSize.get(root) || 0) + 1);
  });

  return pruned.map((m) => {
    const hasLinks = m.twinRelations.length > 0;
    const root = find(m.pid);
    const size = hasLinks ? groupSize.get(root) || 0 : 0;
    if (hasLinks && size >= 2) {
      return { ...m, twinSubGroupId: root, siblingType: sibTypeForSize(size) };
    }
    return clearTwinData(m);
  });
}

function EdgeContextMenu({
  edge,
  user,
  memberRecords,
  setMemberRecords,
  onClose
}) {
  const { setSnack } = React.useContext(SnackContext);

  if (!edge || !EDGE_KINDS.includes(edge.kind)) return null;

  const isRealPid = (pid) => memberRecords.some((m) => m.pid === pid);
  if (
    edge.kind === 'spouse-line' &&
    (!isRealPid(edge.leftPid) || !isRealPid(edge.rightPid))
  ) {
    return null;
  }

  const save = async (affected) => {
    try {
      await axios.post(
        APICONSTANTS.updatePedigreeRelaiton.replace(':user_id', user._id),
        { config: affected.map((m) => ({ data: m })) }
      );
      const byPid = new Map(affected.map((m) => [m.pid, m]));
      setMemberRecords((prev) => prev.map((m) => byPid.get(m.pid) || m));
      onClose();
    } catch (e) {
      console.error('[EdgeContextMenu] remove relation failed:', e);
      setSnack({
        status: true,
        msg: 'Failed to remove relationship. Please try again.',
        severity: 'error'
      });
      onClose();
    }
  };

  const removeChild = () => {
    const childPid = edge.to;
    const parentPids = parseParentIds(edge.from);
    const child = memberRecords.find((m) => m.pid === childPid);
    if (!child) return;

    const origFatherId = child.fatherId ?? null;
    const origMotherId = child.motherId ?? null;
    const hadParent = origFatherId !== null || origMotherId !== null;

    const updatedChild = clearTwinData({
      ...child,
      fatherId: parentPids.includes(child.fatherId) ? null : child.fatherId,
      motherId: parentPids.includes(child.motherId) ? null : child.motherId
    });

    const formerSiblings = hadParent
      ? memberRecords.filter(
          (m) =>
            m.pid !== childPid &&
            (m.fatherId ?? null) === origFatherId &&
            (m.motherId ?? null) === origMotherId
        )
      : [];
    const formerSiblingPids = formerSiblings.map((s) => s.pid);

    updatedChild.siblingIds = (updatedChild.siblingIds || []).filter(
      (id) => !formerSiblingPids.includes(id)
    );

    const affected = [updatedChild];
    const formerSiblingsSibPruned = formerSiblings.map((s) => ({
      ...s,
      siblingIds: (s.siblingIds || []).filter((id) => id !== childPid)
    }));
    pruneAndRederiveTwins(formerSiblingsSibPruned, [childPid]).forEach((s) =>
      affected.push(s)
    );
    parentPids.forEach((ppid) => {
      const parent = memberRecords.find((m) => m.pid === ppid);
      if (parent)
        affected.push({
          ...parent,
          childrenIds: (parent.childrenIds || []).filter(
            (id) => id !== childPid
          )
        });
    });

    save(dedupe(affected));
  };

  const removeChildren = () => {
    const childPids = edge.childrenPids || [];
    const parentPids = parseParentIds(edge.from);
    const childPidSet = new Set(childPids);
    const affected = [];
    const externalsByPid = new Map();

    childPids.forEach((childPid) => {
      const child = memberRecords.find((m) => m.pid === childPid);
      if (!child) return;

      const origFatherId = child.fatherId ?? null;
      const origMotherId = child.motherId ?? null;
      const hadParent = origFatherId !== null || origMotherId !== null;

      const updatedChild = clearTwinData({
        ...child,
        fatherId: parentPids.includes(child.fatherId) ? null : child.fatherId,
        motherId: parentPids.includes(child.motherId) ? null : child.motherId
      });

      const externalFormerSiblings = hadParent
        ? memberRecords.filter(
            (m) =>
              !childPidSet.has(m.pid) &&
              (m.fatherId ?? null) === origFatherId &&
              (m.motherId ?? null) === origMotherId
          )
        : [];

      updatedChild.siblingIds = (updatedChild.siblingIds || []).filter(
        (id) =>
          !childPidSet.has(id) &&
          !externalFormerSiblings.some((s) => s.pid === id)
      );

      affected.push(updatedChild);
      externalFormerSiblings.forEach((s) => {
        const cur = externalsByPid.get(s.pid) || s;
        externalsByPid.set(s.pid, {
          ...cur,
          siblingIds: (cur.siblingIds || []).filter((id) => id !== childPid)
        });
      });
    });

    pruneAndRederiveTwins(
      Array.from(externalsByPid.values()),
      childPids
    ).forEach((s) => affected.push(s));

    parentPids.forEach((ppid) => {
      const parent = memberRecords.find((m) => m.pid === ppid);
      if (parent)
        affected.push({
          ...parent,
          childrenIds: (parent.childrenIds || []).filter(
            (id) => !childPidSet.has(id)
          )
        });
    });

    save(dedupe(affected));
  };

  const removeSpouse = (type) => {
    const removedPid =
      type === 'remove-spouse-left' ? edge.leftPid : edge.rightPid;
    const retainedPid =
      type === 'remove-spouse-left' ? edge.rightPid : edge.leftPid;

    const removedSpouse = memberRecords.find((m) => m.pid === removedPid);
    const retainedSpouse = memberRecords.find((m) => m.pid === retainedPid);
    if (!removedSpouse || !retainedSpouse) return;

    const sharedChildren = memberRecords.filter((m) => {
      const parents = [m.fatherId, m.motherId].filter((p) => p != null);
      return parents.includes(edge.leftPid) && parents.includes(edge.rightPid);
    });
    const sharedChildPidSet = new Set(sharedChildren.map((c) => c.pid));

    // Drop the now-severed partner from each spouse's per-couple
    // spouseRelationships map so it doesn't leave an orphaned key (the matching
    // spouseId is gone). Returns {} when there is nothing to strip, so the
    // field is only overridden when it actually changes.
    const stripSpouseRel = (member, partnerPid) => {
      const next = omitSpouseRelationship(
        member.spouseRelationships,
        partnerPid
      );
      return next === member.spouseRelationships
        ? {}
        : { spouseRelationships: next };
    };

    const affected = [
      {
        ...removedSpouse,
        spouseIds: (removedSpouse.spouseIds || []).filter(
          (id) => id !== retainedPid
        ),
        childrenIds: (removedSpouse.childrenIds || []).filter(
          (id) => !sharedChildPidSet.has(id)
        ),
        ...stripSpouseRel(removedSpouse, retainedPid)
      },
      {
        ...retainedSpouse,
        spouseIds: (retainedSpouse.spouseIds || []).filter(
          (id) => id !== removedPid
        ),
        ...stripSpouseRel(retainedSpouse, removedPid)
      },
      ...sharedChildren.map((child) => ({
        ...child,
        fatherId: child.fatherId === removedPid ? null : child.fatherId,
        motherId: child.motherId === removedPid ? null : child.motherId
      }))
    ];

    save(affected);
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Grow in={!!edge} style={{ transformOrigin: 'top bottom' }}>
        <Box
          sx={{
            width: 170,
            bgcolor: 'white',
            borderRadius: 1,
            border: '1px #777 solid',
            margin: 1
          }}
        >
          <List>
            {edge.kind === 'child' ? (
              <ListItemButton onClick={removeChild}>
                Remove Child
              </ListItemButton>
            ) : edge.kind === 'spouse-line' ? (
              <>
                <ListItemButton
                  onClick={() => removeSpouse('remove-spouse-left')}
                >
                  Remove Spouse on Left
                </ListItemButton>
                <ListItemButton
                  onClick={() => removeSpouse('remove-spouse-right')}
                >
                  Remove Spouse on Right
                </ListItemButton>
              </>
            ) : (
              <ListItemButton onClick={removeChildren}>
                Remove Children
              </ListItemButton>
            )}
          </List>
        </Box>
      </Grow>
    </ClickAwayListener>
  );
}

export default EdgeContextMenu;
